import type { SupabaseClient } from '@supabase/supabase-js';

// libheif-js (WASM build) decodes HEIC/HEIF photos in the browser. Most
// browsers can't decode HEIC natively (only Safari has built-in support).
// Lazy-loaded only when a HEIC file is detected to keep the bundle small.
// @ts-ignore — no type declarations for the wasm-bundle entry
let libheif: any = null;
const loadLibheif = async () => {
  if (!libheif) {
    const mod = await import('libheif-js/wasm-bundle');
    libheif = (mod as any).default || mod;
  }
  return libheif;
};

export const isHeicFile = (file: File): boolean => {
  const lowerType = (file.type || '').toLowerCase();
  if (lowerType === 'image/heic' || lowerType === 'image/heif') return true;
  const ext = file.name.split('.').pop()?.toLowerCase();
  return ext === 'heic' || ext === 'heif';
};

// Convert HEIC/HEIF to JPEG so all browsers can preview & display it.
// Returns the original file if it's not HEIC. Throws on conversion failure.
export const convertHeicToJpeg = async (file: File): Promise<File> => {
  if (!isHeicFile(file)) return file;
  const heif = await loadLibheif();
  const arrayBuffer = await file.arrayBuffer();
  const decoder = new heif.HeifDecoder();
  const images = decoder.decode(new Uint8Array(arrayBuffer));
  if (!images || images.length === 0) throw new Error('No images found in HEIC file');
  const image = images[0];
  const width = image.get_width();
  const height = image.get_height();

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context not available');
  const imageData = ctx.createImageData(width, height);

  await new Promise<void>((resolve, reject) => {
    image.display(imageData, (displayData: any) => {
      if (!displayData) return reject(new Error('HEIF processing error'));
      resolve();
    });
  });

  ctx.putImageData(imageData, 0, 0);

  const jpegBlob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      blob => blob ? resolve(blob) : reject(new Error('Canvas toBlob failed')),
      'image/jpeg',
      0.9
    );
  });
  if (!jpegBlob || jpegBlob.size === 0) throw new Error('HEIC conversion produced empty result');
  const newName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
  return new File([jpegBlob], newName, { type: 'image/jpeg' });
};

// Accepts canonical URIs like "spotify:album:4itKk52E9ZCdWUQcFAkud9" and
// open.spotify.com links (incl. intl- paths and query strings), which are
// normalized to the URI form. Only track/album — a release's URI should never
// point at an artist profile or playlist.
const SPOTIFY_URI_RE = /^spotify:(track|album):[A-Za-z0-9]{22}$/;
const SPOTIFY_LINK_RE = /open\.spotify\.com\/(?:intl-[a-z-]+\/)?(track|album)\/([A-Za-z0-9]{22})/i;

export const normalizeSpotifyUri = (input: string): string | null => {
  const v = (input || '').trim();
  if (!v) return null;
  if (SPOTIFY_URI_RE.test(v)) return v;
  const m = v.match(SPOTIFY_LINK_RE);
  if (m) return `spotify:${m[1].toLowerCase()}:${m[2]}`;
  return null;
};

export const isValidSpotifyUri = (input: string): boolean => normalizeSpotifyUri(input) !== null;

const COVER_MAX_BYTES = 2 * 1024 * 1024;
const COVER_MAX_DIM = 2048;

// Downscale/re-encode an image until it fits under 2 MB. Starts at the
// original size (capped at 2048px) with JPEG q0.85, then steps quality down
// and halves dimensions as needed. PNG/WebP/GIF are converted to JPEG —
// cover art is opaque anyway, so alpha loss is intentional.
export const compressCoverImage = async (file: File): Promise<File> => {
  const jpegFile = await convertHeicToJpeg(file);

  const bitmap = await createImageBitmap(jpegFile).catch(async () => {
    // Fallback for formats createImageBitmap can't decode
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('Could not decode image'));
      el.src = URL.createObjectURL(jpegFile);
    });
    return img;
  });

  const srcW = 'width' in bitmap ? bitmap.width : 0;
  const srcH = 'height' in bitmap ? bitmap.height : 0;
  if (!srcW || !srcH) throw new Error('Could not read image dimensions');

  // Release covers must be square (Spotify artwork is always 1:1).
  // 2% tolerance for off-by-a-pixel sources.
  if (Math.abs(srcW / srcH - 1) > 0.02) {
    throw new Error('Cover must be square (1:1 aspect ratio)');
  }

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context not available');

  let scale = Math.min(1, COVER_MAX_DIM / Math.max(srcW, srcH));
  const qualities = [0.85, 0.75, 0.6, 0.45, 0.3];

  const encode = (s: number, q: number): Promise<Blob> => {
    canvas.width = Math.max(1, Math.round(srcW * s));
    canvas.height = Math.max(1, Math.round(srcH * s));
    ctx.drawImage(bitmap as CanvasImageSource, 0, 0, canvas.width, canvas.height);
    return new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        blob => blob ? resolve(blob) : reject(new Error('Canvas toBlob failed')),
        'image/jpeg',
        q
      );
    });
  };

  // Quality ladder at full size first, then shrink dimensions if needed.
  for (let round = 0; round < 4; round++) {
    for (const q of qualities) {
      const blob = await encode(scale, q);
      if (blob.size <= COVER_MAX_BYTES) {
        const name = file.name.replace(/\.[^.]+$/, '') || 'cover';
        return new File([blob], `${name}.jpg`, { type: 'image/jpeg' });
      }
    }
    scale *= 0.5;
  }

  throw new Error('Could not compress image below 2 MB');
};

// Uploads a compressed cover and points releases.cover_url at the public URL.
// The 'submission-images' bucket is already public and used for other uploads.
export const uploadReleaseCover = async (
  db: SupabaseClient,
  releaseId: string,
  file: File
): Promise<string> => {
  const compressed = await compressCoverImage(file);
  const path = `release-covers/${releaseId}_${Date.now()}.jpg`;

  const { error: uploadError } = await db.storage
    .from('submission-images')
    .upload(path, compressed, { contentType: 'image/jpeg', upsert: false });
  if (uploadError) throw new Error(uploadError.message);

  const { data: urlData } = db.storage.from('submission-images').getPublicUrl(path);

  const { error: updateError } = await db
    .from('releases')
    .update({ cover_url: urlData.publicUrl })
    .eq('id', releaseId);
  if (updateError) throw new Error(updateError.message);

  return urlData.publicUrl;
};

// True when release_date falls inside the current calendar week (Mon–Sun).
export const isReleaseInCurrentWeek = (releaseDate?: string | null): boolean => {
  if (!releaseDate) return false;
  const d = new Date(releaseDate);
  if (isNaN(d.getTime())) return false;
  const now = new Date();
  const day = (now.getDay() + 6) % 7; // Monday = 0
  const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - day);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 7);
  return d >= monday && d < sunday;
};
